import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  JOB_STATUS_LABEL,
  ORDER_STATUS_LABEL,
  adminConfirmOrder,
  adminGetOrder,
  adminRejectOrder,
  adminRetryJob,
  formatYuan,
  isUnauthorized,
  type AdminOrder,
  type Job,
} from '../lib/api';
import AdminNav from './AdminNav';

export default function AdminOrderDetail() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState<AdminOrder | null>(null);
  const [jobs, setJobs] = useState<Job[]>([]);
  const [note, setNote] = useState('');
  const [error, setError] = useState('');
  const [msg, setMsg] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const data = await adminGetOrder(id);
      setOrder(data.order);
      setJobs(data.jobs);
      setNote(data.order.admin_note ?? '');
    } catch (err) {
      if (isUnauthorized(err)) {
        navigate('/admin/login', { replace: true });
        return;
      }
      setError('订单加载失败或不存在');
    }
  }, [id, navigate]);

  useEffect(() => {
    load();
  }, [load]);

  async function onConfirm() {
    if (!order) return;
    setBusy(true);
    setError('');
    setMsg('');
    try {
      await adminConfirmOrder(order.id);
      setMsg('已确认到账，用户可进入创作室');
      await load();
    } catch (err) {
      if (isUnauthorized(err)) {
        navigate('/admin/login', { replace: true });
        return;
      }
      setError('确认失败（状态可能不允许）');
    } finally {
      setBusy(false);
    }
  }

  async function onReject() {
    if (!order) return;
    setBusy(true);
    setError('');
    setMsg('');
    try {
      await adminRejectOrder(order.id, note.trim() || undefined);
      setMsg('已拒绝该订单');
      await load();
    } catch (err) {
      if (isUnauthorized(err)) {
        navigate('/admin/login', { replace: true });
        return;
      }
      setError('拒绝失败（状态可能不允许）');
    } finally {
      setBusy(false);
    }
  }

  async function onRetry(jobId: string) {
    setBusy(true);
    setError('');
    setMsg('');
    try {
      await adminRetryJob(jobId);
      setMsg('已重新入队');
      await load();
    } catch (err) {
      if (isUnauthorized(err)) {
        navigate('/admin/login', { replace: true });
        return;
      }
      setError('重试失败');
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="stack">
      <AdminNav />
      <div className="card">
        <h2>订单详情</h2>
        {error ? <div className="error">{error}</div> : null}
        {msg ? <div className="success">{msg}</div> : null}
        {!order && !error ? <p className="muted">加载中…</p> : null}
        {order ? (
          <>
            <p>
              站内订单号：
              <Link className="mono" to={`/order/${order.public_code}`}>
                {order.public_code}
              </Link>
            </p>
            <p>
              内部 ID：<span className="mono muted">{order.id}</span>
            </p>
            <p>
              状态：
              <span className={`status-pill ${order.status}`}>
                {ORDER_STATUS_LABEL[order.status] ?? order.status}
              </span>
            </p>
            <p>
              档位：{order.tier === 'deep' ? '深度' : '标准'} · 金额{' '}
              {formatYuan(order.amount_cents)}
            </p>
            <p>
              闲鱼单号：
              <span className="mono">{order.xianyu_order_no || '—'}</span>
            </p>
            {order.admin_note ? (
              <p>
                备注：{order.admin_note}
              </p>
            ) : null}
          </>
        ) : null}
      </div>

      {order && order.status === 'pending_confirm' ? (
        <div className="card stack">
          <h2>审核操作</h2>
          <label className="field">
            拒绝备注（可选）
            <textarea
              rows={3}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="例如：闲鱼单号不匹配"
            />
          </label>
          <div className="row">
            <button type="button" className="btn" disabled={busy} onClick={onConfirm}>
              确认到账
            </button>
            <button
              type="button"
              className="btn btn-ghost"
              disabled={busy}
              onClick={onReject}
            >
              拒绝
            </button>
          </div>
        </div>
      ) : null}

      <div className="card">
        <h2>生成任务</h2>
        {jobs.length === 0 ? <p className="muted">暂无任务</p> : null}
        {jobs.length > 0 ? (
          <ul className="admin-job-list">
            {jobs.map((j) => (
              <li key={j.id} className="admin-job-item">
                <div>
                  <span className="mono">{j.id.slice(0, 8)}</span>
                  {' · '}
                  <span className={`status-pill ${j.status}`}>
                    {JOB_STATUS_LABEL[j.status] ?? j.status}
                  </span>
                  {' · 尝试 '}
                  {j.attempt}
                  {j.error ? (
                    <span className="muted"> · {j.error}</span>
                  ) : null}
                </div>
                {j.status === 'failed' ? (
                  <button
                    type="button"
                    className="btn btn-ghost"
                    disabled={busy}
                    onClick={() => onRetry(j.id)}
                  >
                    重试
                  </button>
                ) : null}
              </li>
            ))}
          </ul>
        ) : null}
      </div>

      <div className="row">
        <Link className="btn btn-ghost" to="/admin/orders">
          返回列表
        </Link>
      </div>
    </section>
  );
}
